import {useContext, useState} from "react";
import {EntryPoint, LoginCredentials, LoginResult, MfaData, ResetMfaCredentials, Variant, VariantType} from "./types";
import axios, {AxiosResponse} from "axios";
import {makeLxTranslations, useLxQuery, useLxTranslations} from "@lxo-blocks/react";
import {VariantContext} from "./AuthenticateView";
import {PollingResult} from "./mfa/mfaPush/service/mfaPushService.types";
import {confirmMfaPushLogin} from "./mfa/mfaPush/service/mfaPushService";

export interface AuthenticationHandler {
  onSuccess: (entryPoint: EntryPoint, customRedirect: string | null) => void
  onMfaRequired: (currentLoginCredentials: LoginCredentials, userId: string, mfaRequiredResponse: MfaData) => void
  onResetMfa: () => void
  onVariantLoginSuccess: (entryPoint: EntryPoint, customRedirect: string | null, variant: Variant) => void
  onChangeMethod: () => void
}

export interface UserAuthenticateResult {
  authenticate: (loginCredentials: LoginCredentials) => void;
  authenticateByTicket: (confirmedPollingTicket: PollingResult, rememberDevice: boolean) => void;
  resetMfa: (resetMfaCredentials: ResetMfaCredentials) => void;
  authenticationError?: any;
}

const AUTHENTICATION_TRANSLATION = makeLxTranslations({
  namespace: "authenticationTranslations",
  translations: {
    LOGIN_RATE_LIMIT: "lexoffice hat eine ungewöhnlich hohe Anzahl von Anmeldungen festgestellt." +
      " Aus Sicherheitsgründen werden diese Anmeldedaten vorübergehend blockiert." +
      " Bitte versuchen Sie es in {{timeWindowInMinutes}} Minute(n) erneut",
  }
})

export const useAuthenticate = (authenticationHandler?: AuthenticationHandler): UserAuthenticateResult => {
  const customRedirect = useLxQuery().get("redirect");
  const {t} = useLxTranslations(AUTHENTICATION_TRANSLATION);
  const [authenticationError, setAuthenticationError] = useState<any>(undefined);
  const variant = useContext(VariantContext);

  const authenticate = async (loginCredentials: LoginCredentials) => {
    setAuthenticationError(undefined);
    let loginResult: AxiosResponse<LoginResult>
    try {
      loginResult = await axios.post("/grld-public/login/v2/authorize", loginCredentials);
    } catch (error) {
      handleError(error, loginCredentials);
      return;
    }
    if (!variant) {
      authenticationHandler?.onSuccess(loginResult.data.entrypoint, customRedirect);
    } else {
      authenticationHandler?.onVariantLoginSuccess(loginResult.data.entrypoint, customRedirect, variant);
    }
  }

  const authenticateByTicket = async (confirmedPollingTicket: PollingResult, rememberDevice: boolean) => {
    setAuthenticationError(undefined);
    confirmMfaPushLogin(confirmedPollingTicket, rememberDevice, {
      onSuccess(loginResult: any): void {
        if (!variant) {
          authenticationHandler?.onSuccess(loginResult.data.entrypoint, customRedirect);
        } else {
          authenticationHandler?.onVariantLoginSuccess(loginResult.data.entrypoint, customRedirect, variant);
        }
      },
      onError(error: any): void {
        setAuthenticationError(error);
      }
    })
  }

  const resetMfa = async (resetMfaCredentials: ResetMfaCredentials) => {
    try {
      setAuthenticationError(undefined);
      let loginResult: AxiosResponse<LoginResult> = await axios.post("/grld-public/login/authorizeResetMfa", resetMfaCredentials);
      authenticationHandler?.onSuccess(loginResult.data.entrypoint, customRedirect);
    } catch (error) {
      handleMfaResetError(error);
    }
  }

  const handleError = (error: any, loginCredentials: LoginCredentials) => {
    switch (error.response.status) {
      case 423: // MFA Required
        const userId = error.response.data.userId ? error.response.data.userId : error.response.data;
        authenticationHandler?.onMfaRequired(loginCredentials, userId, error.response.data.userId ? error.response.data : null);
        break;
      case 429: // RateLimit
        handleRateLimit(error.response.data);
        break;
      default:
        setAuthenticationError(error.response);
    }
  }

  const handleMfaResetError = (error: any) => {
    switch (error.response.status) {
      case 429: // RateLimit
        handleRateLimit(error.response.data);
        break;
      default:
        setAuthenticationError(error.response);
    }
  }

  const handleRateLimit = (rateLimitData: any) => {
    const timeWindowInMinutes = rateLimitData.timeToWaitInMinutes;

    setAuthenticationError({
      description: t("LOGIN_RATE_LIMIT", {timeWindowInMinutes: timeWindowInMinutes})
    });
  }

  return {
    authenticate: authenticate,
    authenticateByTicket: authenticateByTicket,
    resetMfa: resetMfa,
    authenticationError: authenticationError
  }
}

// Normal relative paths are allowed.
const leadingSlashPattern = /^\//;
// A domain with 2 slashes prepended is interpreted as new domain in a redirect (//lexoffice.de -> https://lexoffice.de)
const doubleLeadingSlashPattern = /^\/\//;
// Backslashes might be used to mask the second slash.
const doubleMaskedLeadingSlashPattern = /^\/(\\)*\//;

// Export this function to enable testing
/**
 * Check if a given `customRedirectUrl` is allowed. That is the case when it is a simple relative url.
 * We exclude other cases that might lead to an open redirect.
 *
 * @param customRedirectUrl
 */
export const isAllowedRedirectUrl = (customRedirectUrl: string): boolean => {
  return customRedirectUrl.length > 0
    && leadingSlashPattern.test(customRedirectUrl)
    && !doubleLeadingSlashPattern.test(customRedirectUrl)
    && !doubleMaskedLeadingSlashPattern.test(customRedirectUrl);
};

// Export this function to enable testing
/**
 * We use a full url (protocol, host, port, path) to always stay on our own domain.
 *
 * @param customRedirectUrl
 */
export const getFullUrlForCustomRedirect = (customRedirectUrl: string) => {
  // Contains protocol, hostname and port
  const fullDomainName = window.location.origin;
  return `${fullDomainName}${customRedirectUrl}`;
};

export const redirect = (entryPoint: EntryPoint, customRedirect: string | null) => {
  // Priority 1: Explicitly set custom redirect
  if (customRedirect && isAllowedRedirectUrl(customRedirect)) {
    const fullRedirectUrl = getFullUrlForCustomRedirect(customRedirect);
    window.location.href = fullRedirectUrl;
    return;
  }

  // Priority 2: CustomDestinationUrl calculated by backend
  if (entryPoint.destination === "CUSTOM_DESTINATION"
    && entryPoint.customDestinationUrl
    && isAllowedRedirectUrl(entryPoint.customDestinationUrl)) {
    const fullRedirectUrl = getFullUrlForCustomRedirect(entryPoint.customDestinationUrl);
    window.location.href = fullRedirectUrl;
    return;
  }

  // Priority 3: Entrypoint calculated by backend
  switch (entryPoint.destination) {
    case "PAYROLL_DASHBOARD":
      window.location.href = "/payroll/#/";
      break;
    case "PAYROLL_SELF_SERVICE_DASHBOARD":
      window.location.href = "/selfservices/";
      break;
    case "TAXADVISOR_DASHBOARD":
      window.location.href = "/tax-advisor/#/";
      break;
    case "PAYROLL_CONSULTANT_BOARD":
      window.location.href = "/payroll/consultant.html";
      break;
    case "GRLD_DASHBOARD":
    default:
      window.location.href = "/dashboard/#/"
      break;
  }
}

export const redirectVariant = (entryPoint: EntryPoint, customRedirect: string | null, variant: Variant) => {
  switch (variant.type) {
    case VariantType.USERVOICE:
      redirectUserVoiceSso();
      break;
    case  VariantType.SPARKASSE:
    default:
      // use standard redirect
      redirect(entryPoint, customRedirect);
      break;
  }
}

const redirectUserVoiceSso = () => {
  axios.get("/grld-rest/account/v100").then((accountData) => {
    window.location.href = "https://lexoffice.uservoice.com/login_success?sso=" + accountData.data.user.userVoiceToken
  })
}
